import { Lang, Variant as RawVariant, RewardOptionValue } from '@owl-nest/api-client/latest'
import * as env from '@owl-nest/config'

import * as i18n from './i18n'
import * as rewardModel from './reward'
import * as imageModel from './image'

export type Variant<PROJECT_EXTRA_FIELDS extends string = ''> = Omit<RawVariant, 'parent'> & {
  parent: rewardModel.Reward<PROJECT_EXTRA_FIELDS>
}

export function Variant<PROJECT_EXTRA_FIELDS extends string = ''>(
  variant: RawVariant,
  parent: rewardModel.Reward<PROJECT_EXTRA_FIELDS>,
): Variant<PROJECT_EXTRA_FIELDS> {
  return { ...variant, parent }
}

/**
 * Returns the variant description in the user's locale. If none, falls back to the project language.
 * @param variant
 */
export function description(variant: Variant<any>): string {
  return i18n.get(variant.description, variant.parent.project?.lang as Lang, '')
}

/**
 * Groups the variant stock data in a handy format.
 * @param variant
 */
export function stock(variant: Variant<any>): rewardModel.RewardStock {
  return {
    available: variant.aware_stock_available,
    taken: variant.stock_taken,
    total: variant.aware_stock,
  }
}

/**
 * Returns the great variant image, from combined or not
 * @param variant
 */
export function image(
  variant: Variant,
  version = 'full',
  lang: Lang = env.getUserConfig().lang,
  rewardOptionValues: RewardOptionValue[] = [],
): string | undefined {
  const optionValues = rewardOptionValues.length > 0 ? rewardOptionValues : (values(variant) ?? [])

  if (variant.image !== undefined) {
    const imageResource = i18n.getWithLang(variant.image, lang)
    return imageModel.getUrl(imageResource?.versions, version)
  }

  if (variant.value_ids !== undefined && variant.parent !== undefined) {
    if (optionValues.length > 0) {
      let i = optionValues.length - 1

      while (i >= 0) {
        const image = optionValues[i].image
        if (image) {
          const imageResource = i18n.getWithLang(image, lang)
          return imageModel.getUrl(imageResource?.versions, version)
        }
        i--
      }
    }
  }

  if (variant.parent.image) {
    const imageResource = i18n.getWithLang(variant.parent.image, lang)
    return imageModel.getUrl(imageResource?.versions, version)
  }

  return ''
}

export function values(variant: Variant): RewardOptionValue[] | undefined {
  if (!variant.parent || !variant.value_ids) return undefined

  return variant.value_ids.flatMap(
    (valueId) =>
      variant.parent.options?.flatMap((option) =>
        option.values.filter((value) =>
          valueId.id !== undefined ? valueId.id === value.id : valueId.identifier === value.identifier,
        ),
      ) ?? [],
  )
}
